import React, { useState, useEffect, useRef, useMemo } from 'react'
import { getSharedContent, SharedResearchResponse, saveResearchArticle, unsaveResearchArticle, fetchSavedResearch, API_BASE_URL } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { GoogleLogin } from '../components/GoogleLogin'
import { AppleLogin } from '../components/AppleLogin'
import type { AppleLoginPayload } from '../lib/authTypes'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import { CockpitRuntime } from '../components/cockpit/CockpitRuntime'
import { compileViews } from '../lib/cockpit/compiler/viewCompiler'
import { DEFAULT_PERSONAS, type CIG } from '../lib/cockpit/schemas'
import { normalizeLegacyMetrics } from '../lib/cockpit/normalizers'
import { buildCanonicalArticleShareUrl, buildCanonicalReportShareUrl } from '../lib/shareUrls'

interface SharedArticlePageProps {
  isDarkMode: boolean
  onShowToast: (toast: { type: 'success' | 'error' | 'info'; title: string; description?: string }) => void
}

interface Section {
  id: string
  title: string
  level: number
}

export default function SharedArticlePage({ isDarkMode, onShowToast }: SharedArticlePageProps) {
  const { user, loginWithGoogle, loginWithApple } = useAuth()
  
  const getShareId = () => {
    const path = window.location.pathname
    const match = path.match(/^\/s\/([a-zA-Z0-9]+)$/)
    return match ? match[1] : null
  }
  
  const shareId = getShareId()
  
  const navigate = (path: string) => {
    window.location.href = path
  }
  const [sharedContent, setSharedContent] = useState<SharedResearchResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSignupPrompt, setShowSignupPrompt] = useState(false)
  const [sections, setSections] = useState<Section[]>([])
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [isSaved, setIsSaved] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const pendingFullReportNavRef = useRef(false)

  const completeLogin = async (loginAction: () => Promise<void>) => {
    try {
      await loginAction()
      onShowToast({ type: 'success', title: 'Welcome to Insight Atlas!', description: 'You have been successfully logged in.' })
      setShowSignupPrompt(false)
    } catch (err) {
      onShowToast({ type: 'error', title: 'Login failed', description: err instanceof Error ? err.message : 'Please try again.' })
    }
  }

  const handleGoogleLogin = async (credential: string) => {
    await completeLogin(() => loginWithGoogle(credential))
  }

  const handleAppleLogin = async (payload: AppleLoginPayload) => {
    await completeLogin(() => loginWithApple(payload))
  }

  useEffect(() => {
    const loadSharedContent = async () => {
      if (!shareId) return

      setIsLoading(true)
      setError(null)

      try {
        const data = await getSharedContent(shareId)
        if (!data || !data.research) {
          throw new Error('Invalid shared content payload')
        }
        
        setSharedContent(data)
        
        if (data.research?.title) {
          document.title = `${data.research.title} - InsightAtlas`
        }
      } catch (err) {
        console.error('Failed to load shared content:', err)
        setError('Failed to load shared article. The link may be invalid or expired.')
      } finally {
        setIsLoading(false)
      }
    }

    if (shareId) {
      loadSharedContent()
    }
  }, [shareId])

  // Check if article is saved when user logs in
  useEffect(() => {
    const checkIfSaved = async () => {
      if (user && sharedContent) {
        try {
          const savedArticles = await fetchSavedResearch()
          const isArticleSaved = savedArticles.some(
            article => article.research_id?.toString() === sharedContent.research.id || 
                      article.id?.toString() === sharedContent.research.id
          )
          setIsSaved(isArticleSaved)
        } catch (error) {
          console.error('Failed to check saved status:', error)
        }
      }
    }
    
    checkIfSaved()
  }, [user, sharedContent])

  const goToFullReport = async () => {
    try {
      if (!sharedContent) return
      const { research, share_info } = sharedContent
      if (share_info?.permission_type === 'public') {
        const canonicalUrl = (() => {
          const reportId = research.intelligence_report_id?.trim()
          if (reportId) {
            return buildCanonicalReportShareUrl(reportId)
          }

          const rawResearchId = research.id
          const researchId = typeof rawResearchId === 'number' ? rawResearchId : Number(rawResearchId)
          if (Number.isInteger(researchId) && researchId > 0) {
            return buildCanonicalArticleShareUrl(researchId)
          }

          return null
        })()

        if (!canonicalUrl) {
          throw new Error('Unable to determine a canonical URL for this shared research.')
        }

        window.location.assign(canonicalUrl)
        return
      }
      if (shareId) {
        window.location.assign(`${API_BASE_URL}/s/${shareId}`)
      }
    } catch (e) {
      console.error('Failed to open full report:', e)
      onShowToast({ type: 'error', title: 'Unable to open full report', description: e instanceof Error ? e.message : undefined })
    }
  }

  useEffect(() => {
    if (user && pendingFullReportNavRef.current) {
      pendingFullReportNavRef.current = false
      void goToFullReport()
    }
  }, [user])

  // Detect intelligence report mode
  const isIntelligenceReport = !!(sharedContent?.research?.report_type === 'intelligence' && sharedContent.research.cig && sharedContent.research.sib)

  // Compile CockpitRuntime data for intelligence reports
  const cockpitData = useMemo(() => {
    if (!isIntelligenceReport || !sharedContent) return null
    
    try {
      const research = sharedContent.research
      const cig = research.cig || {}
      const sib = research.sib || {}
      const scenarioPackage = research.scenario
      const cigMetadata = (cig as any).metadata || {}
      
      // Build scenario axes (same logic as HomePage)
      const scenarioAxes = (() => {
        if (!scenarioPackage?.axis) return (cig as any).scenario_axes ?? []
        const axis = scenarioPackage.axis
        return [{
          axis_id: axis.axis_id || 'default',
          label: axis.label || 'Scenario Analysis',
          description: axis.description,
          states: (axis.states || []).map((state: any) => ({
            state_id: state.state_id,
            label: state.label,
            short_description: state.short_description || state.description,
            is_default: state.is_default,
            probability_band: state.probability_band,
            triggers: (state.triggers || []).map((t: any) => ({ trigger_id: t.trigger_id || t.id, description: t.description, observability: t.observability, confidence: t.confidence })),
            assumptions: (state.assumptions || []).map((a: any) => ({ assumption_id: a.assumption_id || a.id, text: a.text, rationale: a.rationale })),
            metric_impacts: state.metric_impacts || [],
          })),
          triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((t: any) => ({ id: t.trigger_id || t.id, label: t.label || t.description, definition: t.definition || t.description, indicator_type: t.indicator_type, maps_to_state_transitions: t.maps_to_state_transitions, confidence: t.confidence })),
          assumptions: (axis.assumptions || []).map((a: any) => ({ id: a.assumption_id || a.id, text: a.text, applies_to_state_id: a.applies_to_state_id })),
        }]
      })()

      // Determine best title
      const sibTitle = sib?.research_title
      const cigTitle = cigMetadata.title
      const userQuery = research.title || research.query || ''
      let bestTitle = userQuery
      if (sibTitle && sibTitle.length > 10 && sibTitle !== userQuery) {
        bestTitle = sibTitle
      } else if (cigTitle && cigTitle.length > 10 && cigTitle !== userQuery) {
        bestTitle = cigTitle
      }

      // Build enhanced CIG
      const enhancedCig: CIG = {
        ...cig,
        metadata: {
          ...cigMetadata,
          schema_version: cigMetadata.schema_version || '2.2',
          report_id: cigMetadata.report_id || research.intelligence_report_id || 'shared-report',
          title: bestTitle,
        },
        sources: (cig as any).sources ?? [],
        claims: (cig as any).claims ?? [],
        entities: (cig as any).entities ?? [],
        metrics: normalizeLegacyMetrics((cig as any).metrics as any),
        relationships: (cig as any).relationships ?? [],
        figures: (cig as any).figures ?? [],
        datasets: (cig as any).datasets ?? [],
        watchlist: (cig as any).watchlist ?? [],
        key_findings: (cig as any).key_findings ?? [],
        executive_summary: (cig as any).executive_summary ?? [],
        bluf: ((cig as any).bluf ?? research.title) || research.query,
        report_sections: (cig as any).report_sections ?? [],
        scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : (cig as any).scenario_axes,
      }

      const persona = DEFAULT_PERSONAS.find(p => p.persona_id === 'general_exec') || DEFAULT_PERSONAS[0]
      
      // Build deep research payload
      const deepResearch = research.deep_research ? {
        text: research.deep_research.text || research.deep_research_report || research.written_report || research.report_text || '',
        sources: research.deep_research.sources || [],
        citations: research.deep_research.citations || [],
        status: research.deep_research.status,
        provider: research.deep_research.provider,
      } : (research.written_report || research.deep_research_report || research.report_text) ? {
        text: research.written_report || research.deep_research_report || research.report_text || '',
      } : undefined

      const views = compileViews(enhancedCig, persona, sib, deepResearch)
      const heroImageUrl = research.image_url

      const studyMeta = {
        id: research.intelligence_report_id ? `ia-${research.intelligence_report_id}` : String(research.id),
        intelligenceReportId: research.intelligence_report_id || undefined,
        researchId: research.id,
        isSaved: isSaved,
        isPublic: true,
        category: research.category,
        createdAt: research.created_at,
        query: research.query || research.title,
        researcher: sharedContent.share_info.owner_name,
        researcherEmail: (sharedContent.share_info as any).owner_email,
        researcherAvatarUrl: research.author?.avatar,
      }

      return { enhancedCig, views, persona, heroImageUrl, studyMeta }
    } catch (error) {
      console.error('Error compiling cockpit views for shared page:', error)
      return null
    }
  }, [isIntelligenceReport, sharedContent, isSaved])

  // Extract sections from content for table of contents (legacy article view only)
  useEffect(() => {
    if (isIntelligenceReport) return
    if (sharedContent?.research?.results?.report_text || (sharedContent?.research?.results as any)?.content) {
      const content = sharedContent?.research?.results?.report_text || (sharedContent?.research?.results as any)?.content || ''
      const allSections: Section[] = []
      const seenTitles = new Set<string>()
      
      const markdownHeaders = content.matchAll(/^[ \t]*(#{1,6})[ \t]+(.+)$/gm)
      for (const match of markdownHeaders) {
        const level = match[1].length
        let title = match[2].trim()
        title = title.replace(/\*\*\*(.*?)\*\*\*/g, '$1')
        title = title.replace(/\*\*(.*?)\*\*/g, '$1')
        title = title.replace(/\*(.*?)\*/g, '$1')
        title = title.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        title = title.replace(/`([^`]+)`/g, '$1')
        title = title.replace(/\([\d,+\s\w]+\)$/, '').trim()
        
        if (title && !seenTitles.has(title)) {
          seenTitles.add(title)
          const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
          allSections.push({ id, title, level: Math.min(level, 4) })
        }
      }
      
      const boldHeaders = content.matchAll(/^[ \t]*\*\*([^*\n]+)\*\*[ \t]*$/gm)
      for (const match of boldHeaders) {
        const title = match[1].trim()
        if (title && 
            !title.endsWith('.') && 
            !title.endsWith(',') && 
            !title.endsWith(';') && 
            title.length < 100 &&
            !seenTitles.has(title) &&
            (title.includes(':') || 
             title.match(/^[A-Z]/) || 
             title.includes('Summary') ||
             title.includes('Finding') ||
             title.includes('Analysis') ||
             title.includes('Background'))) {
          seenTitles.add(title)
          const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
          allSections.push({ 
            id, 
            title: title.replace(/:$/, ''),
            level: 2 
          })
        }
      }
      
      const specialSections: Section[] = []
      if (sharedContent?.research?.summary && !allSections.some(s => 
        s.title.toLowerCase().includes('executive summary'))) {
        specialSections.push({ id: 'executive-summary', title: 'Executive Summary', level: 2 })
      }
      if (sharedContent?.research?.results?.key_findings?.length && !allSections.some(s => 
        s.title.toLowerCase().includes('key finding') || 
        s.title.toLowerCase().includes('key takeaway'))) {
        specialSections.push({ id: 'key-findings', title: 'Key Findings', level: 2 })
      }
      
      setSections([...specialSections, ...allSections])
    }
  }, [sharedContent, isIntelligenceReport])

  // Intersection observer for active section (legacy view only)
  useEffect(() => {
    if (isIntelligenceReport || !sections.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )

    sections.forEach((section) => {
      const element = document.getElementById(section.id)
      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [sections, isIntelligenceReport])
  
  // Set Open Graph meta tags for link previews
  useEffect(() => {
    if (sharedContent?.research) {
      const { research } = sharedContent
      document.title = `${research.title} - InsightAtlas`
      
      const setMetaTag = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('property', property)
          document.head.appendChild(meta)
        }
        meta.content = content
      }
      
      setMetaTag('og:title', research.title)
      setMetaTag('og:description', research.summary || research.query)
      setMetaTag('og:type', 'article')
      setMetaTag('og:url', window.location.href)
      setMetaTag('og:site_name', 'InsightAtlas')
      
      let imageUrl = research.image_url || `https://www.insightatlas.ai/api/og-image?title=${encodeURIComponent(research.title)}`
      if (imageUrl && imageUrl.startsWith('/')) {
        const baseUrl = window.location.origin.includes('localhost') 
          ? 'http://localhost:8000' 
          : 'https://www.insightatlas.ai'
        imageUrl = baseUrl + imageUrl
      }
      
      setMetaTag('og:image', imageUrl)
      setMetaTag('og:image:width', '1200')
      setMetaTag('og:image:height', '630')
      setMetaTag('og:image:alt', research.title)
      setMetaTag('twitter:card', 'summary_large_image')
      setMetaTag('twitter:title', research.title)
      setMetaTag('twitter:description', research.summary || research.query)
      setMetaTag('twitter:image', imageUrl)
      setMetaTag('twitter:site', '@insightatlas')
      
      const setNameMetaTag = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('name', name)
          document.head.appendChild(meta)
        }
        meta.content = content
      }
      
      setNameMetaTag('description', research.summary || research.query)
      setNameMetaTag('author', research.author?.name || 'InsightAtlas')
      
      return () => { document.title = 'InsightAtlas' }
    }
  }, [sharedContent])

  const handleSaveArticle = async () => {
    if (!user) {
      setShowSignupPrompt(true)
      return
    }
    
    if (sharedContent) {
      try {
        if (isSaved) {
          const success = await unsaveResearchArticle(sharedContent.research.id)
          if (success) {
            setIsSaved(false)
            onShowToast({ type: 'success', title: 'Removed', description: `${sharedContent.research.title} has been removed from your collection.` })
          } else {
            onShowToast({ type: 'error', title: 'Remove Failed', description: 'Unable to remove article. Please try again.' })
          }
        } else {
          const success = await saveResearchArticle(sharedContent.research.id)
          if (success) {
            setIsSaved(true)
            onShowToast({ type: 'success', title: 'Saved!', description: `${sharedContent.research.title} has been saved to your collection.` })
          } else {
            onShowToast({ type: 'error', title: 'Save Failed', description: 'Unable to save article. Please try again.' })
          }
        }
      } catch (error) {
        console.error('Failed to save/unsave article:', error)
        onShowToast({ type: 'error', title: 'Operation Failed', description: 'Unable to complete the operation. Please try again.' })
      }
    }
  }

  const handleStartResearch = () => {
    if (!user) {
      setShowSignupPrompt(true)
      return
    }
    navigate('/research')
  }

  const handleReadFullReport = () => {
    const isPublicShare = sharedContent?.share_info?.permission_type === 'public'
    if (!user && !isPublicShare) {
      pendingFullReportNavRef.current = true
      setShowSignupPrompt(true)
      return
    }
    void goToFullReport()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <LoadingAnimation size={64} />
          </div>
          <p className="text-gray-600 dark:text-gray-400">Loading shared research...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Unable to Load Content
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    )
  }

  if (!sharedContent) return null

  const { share_info, research } = sharedContent

  // Intelligence Report: render full CockpitRuntime
  if (isIntelligenceReport && cockpitData) {
    return (
      <>
        {/* Top Banner for non-logged in users */}
        {!user && (
          <div className="bg-blue-600 text-white py-3 px-4 text-center relative z-50">
            <p className="text-sm">
              Sign up to save this article and create your own AI-powered research
              <button
                onClick={() => setShowSignupPrompt(true)}
                className="ml-3 bg-white text-blue-600 px-4 py-1 rounded-full text-sm font-medium hover:bg-blue-50 transition-colors"
              >
                Sign Up Free
              </button>
            </p>
          </div>
        )}

        {/* Full CockpitRuntime */}
        <div className={`${!user ? '' : ''} bg-gray-50 dark:bg-gray-900`} style={{ height: !user ? 'calc(100vh - 44px)' : '100vh' }}>
          <div className="h-full overflow-y-auto">
            <CockpitRuntime
              cig={cockpitData.enhancedCig}
              views={cockpitData.views}
              persona={cockpitData.persona}
              onBack={() => navigate('/')}
              viewMode="desktop"
              studyMeta={cockpitData.studyMeta}
              heroImageUrl={cockpitData.heroImageUrl}
            />
          </div>
        </div>

        {/* Signup Modal */}
        {showSignupPrompt && !user && (
          <>
            <div 
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100] transition-all duration-300"
              onClick={() => setShowSignupPrompt(false)}
            />
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div 
                className="bg-white dark:bg-gray-900 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform transition-all duration-500 relative mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowSignupPrompt(false)}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Enjoyed this research?
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Create your free account to unlock powerful AI research tools
                  </p>
                </div>

                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Save and organize research articles</p>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Create your own AI-powered research</p>
                  </div>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Get personalized insights and recommendations</p>
                  </div>
                </div>

                <div className="w-full space-y-3">
                  <GoogleLogin 
                    onSuccess={handleGoogleLogin}
                    onError={(error) => {
                      onShowToast({ type: 'error', title: 'Sign up failed', description: error })
                    }}
                  />
                  <AppleLogin
                    onSuccess={handleAppleLogin}
                    onError={(error) => {
                      onShowToast({ type: 'error', title: 'Sign up failed', description: error })
                    }}
                  />
                </div>

                <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
                  Already have an account?{' '}
                  <button
                    onClick={() => { setShowSignupPrompt(false); navigate('/login') }}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Log in
                  </button>
                </p>
              </div>
            </div>
          </>
        )}
      </>
    )
  }

  // Legacy article view for non-intelligence reports
  return (
    <>
      {/* Top Banner for non-logged in users */}
      {!user && (
        <div className="bg-blue-600 text-white py-3 px-4 text-center">
          <p className="text-sm">
            Sign up to save this article and create your own AI-powered research
            <button
              onClick={() => setShowSignupPrompt(true)}
              className="ml-3 bg-white text-blue-600 px-4 py-1 rounded-full text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              Sign Up Free
            </button>
          </p>
        </div>
      )}

      {/* Share Info Bar */}
      <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Shared by <span className="font-medium text-gray-900 dark:text-gray-100">{share_info.owner_name}</span>
              {share_info.permission_type === 'private' && ' \u2022 Private share'}
              {share_info.permission_type === 'link_only' && ' \u2022 Link access'}
            </p>
          </div>
          {share_info.is_owner && (
            <button
              onClick={() => navigate(`/article/${research.id}`)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Manage Share
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative">
        {/* Table of Contents - Desktop only */}
        {sections.length > 0 && (
          <aside className="hidden lg:block fixed left-4 top-32 w-64 z-30">
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 uppercase tracking-wide">
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={(e) => {
                      e.preventDefault()
                      const element = document.getElementById(section.id)
                      if (element) {
                        const yOffset = -100
                        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
                        window.scrollTo({ top: y, behavior: 'smooth' })
                      }
                    }}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    style={{ paddingLeft: `${(section.level - 1) * 16 + 12}px` }}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <article>
        {/* Article Header */}
            <header className="mb-8 sm:mb-12">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
            {research.title}
          </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Published {new Date(research.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                {research.author && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>By {research.author.name}</span>
                  </div>
                )}
                {research.view_count && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{research.view_count.toLocaleString()} views</span>
                  </div>
            )}
          </div>
            </header>

            {/* Action Buttons - Top */}
            <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={handleSaveArticle}
                  className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-medium text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border ${
                    isSaved 
                      ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700' 
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {user ? (isSaved ? 'Saved to Library' : 'Save to Library') : 'Sign Up to Save'}
                </button>
                
                <button
                  onClick={handleStartResearch}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 sm:px-6 py-3 rounded-xl font-medium text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Create Your Own Research
                </button>
                <button
                  onClick={handleReadFullReport}
                  className="flex-1 bg-black dark:bg-white text-white dark:text-black px-4 sm:px-6 py-3 rounded-xl font-semibold text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border border-black dark:border-white"
                  title="Read full report"
                >
                  Read Full Report
                </button>
        </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg dark:prose-invert max-w-none" ref={contentRef}>
              {/* Executive Summary */}
              {research.summary && (
                <section id="executive-summary" className="mb-8 sm:mb-12">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 p-4 sm:p-6 lg:p-8 rounded-r-xl executive-summary-box">
                    <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900 dark:text-white">Executive Summary</h2>
                    <div 
                      className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 leading-relaxed executive-summary-content"
                      dangerouslySetInnerHTML={{ 
                        __html: research.summary
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match: string, text: string, url: string) => {
                            const finalUrl = url.startsWith('http') ? url : 'https://' + url;
                            return `${text} <a href="${finalUrl}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ml-0.5" target="_blank" rel="noopener noreferrer" title="${finalUrl}"><svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                          })
                          .replace(/\([^)]*(?:https?:\/\/|www\.)[^)]+\)/g, (match: string) => {
                            const content = match.slice(1, -1);
                            const urlMatch = content.match(/((?:https?:\/\/)?(?:www\.)?[^\s]+\.[^\s]+)/);
                            if (urlMatch) {
                              let url = urlMatch[1].trim();
                              if (!url.startsWith('http')) url = 'https://' + url;
                              return ` <a href="${url}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" target="_blank" rel="noopener noreferrer" title="${url}"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                            }
                            return ''
                          })
                          .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                          .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
                      }}
                    />
                  </div>
                </section>
              )}
              
              {/* Key Findings */}
          {research.results?.key_findings && research.results.key_findings.length > 0 && (
                <section id="key-findings" className="mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white flex items-center gap-2">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Key Findings
                  </h2>
                  <ol className="space-y-3 sm:space-y-4">
                {research.results.key_findings.map((finding, index) => (
                      <li key={index} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                        <div 
                          className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed flex-1"
                          dangerouslySetInnerHTML={{ 
                            __html: finding
                              .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match: string, text: string, url: string) => {
                                const finalUrl = url.startsWith('http') ? url : 'https://' + url;
                                return `${text} <a href="${finalUrl}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ml-0.5" target="_blank" rel="noopener noreferrer" title="${finalUrl}"><svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                              })
                              .replace(/\([^)]*(?:https?:\/\/|www\.)[^)]+\)/g, (match: string) => {
                                const content = match.slice(1, -1);
                                const urlMatch = content.match(/((?:https?:\/\/)?(?:www\.)?[^\s]+\.[^\s]+)/);
                                if (urlMatch) {
                                  let url = urlMatch[1].trim();
                                  if (!url.startsWith('http')) url = 'https://' + url;
                                  return ` <a href="${url}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" target="_blank" rel="noopener noreferrer" title="${url}"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                                }
                                return ''
                              })
                              .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
                              .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
                          }}
                        />
                  </li>
                ))}
                  </ol>
                </section>
              )}
              
              {/* Full Report */}
              {(research.results?.report_text || (research.results as any)?.content) && (
                <section className="mb-8 sm:mb-12">
                  <div 
                    className="research-content text-base sm:text-lg"
                    style={{
                      fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
                      lineHeight: '1.75'
                    }}
                dangerouslySetInnerHTML={{ 
                      __html: (research.results?.report_text || (research.results as any)?.content || '')
                        .replace(/^##?\s*Executive Summary\s*\n+/gim, '')
                        .replace(/^### (.+)$/gim, (_match: string, text: string) => {
                          let cleanText = text.trim()
                          cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                          cleanText = cleanText.replace(/\*\*\*(.*?)\*\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*\*(.*?)\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*(.*?)\*/g, '$1')
                          const id = cleanText.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                          return `<h3 id="${id}" class="text-lg sm:text-xl font-bold mt-6 sm:mt-8 mb-3 sm:mb-4 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">${text}</h3>`
                        })
                        .replace(/^## (.+)$/gim, (_match: string, text: string) => {
                          let cleanText = text.trim()
                          cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                          cleanText = cleanText.replace(/\*\*\*(.*?)\*\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*\*(.*?)\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*(.*?)\*/g, '$1')
                          const id = cleanText.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                          return `<h2 id="${id}" class="text-xl sm:text-2xl font-bold mt-8 sm:mt-10 mb-4 sm:mb-6 text-gray-900 dark:text-white border-l-4 border-blue-500 pl-3 sm:pl-4">${text}</h2>`
                        })
                        .replace(/^# (.+)$/gim, (_match: string, text: string) => {
                          let cleanText = text.trim()
                          cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                          cleanText = cleanText.replace(/\*\*\*(.*?)\*\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*\*(.*?)\*\*/g, '$1')
                          cleanText = cleanText.replace(/\*(.*?)\*/g, '$1')
                          const id = cleanText.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                          return `<h1 id="${id}" class="text-2xl sm:text-3xl font-bold mt-10 sm:mt-12 mb-6 sm:mb-8 text-gray-900 dark:text-white">${text}</h1>`
                        })
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match: string, text: string, url: string) => {
                          const finalUrl = url.startsWith('http') ? url : 'https://' + url;
                          return `${text} <a href="${finalUrl}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 ml-0.5" target="_blank" rel="noopener noreferrer" title="${finalUrl}"><svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                        })
                        .replace(/\([^)]*(?:https?:\/\/|www\.)[^)]+\)/g, (match: string) => {
                          const content = match.slice(1, -1);
                          const urlMatch = content.match(/((?:https?:\/\/)?(?:www\.)?[^\s]+\.[^\s]+)/);
                          if (urlMatch) {
                            let url = urlMatch[1].trim();
                            if (!url.startsWith('http')) url = 'https://' + url;
                            return ` <a href="${url}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" target="_blank" rel="noopener noreferrer" title="${url}"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                          }
                          return ''
                        })
                    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .split(/\n\n+/).map((paragraph: string) => {
                          const trimmed = paragraph.trim()
                          if (!trimmed) return ''
                          if (trimmed.includes('<h1') || trimmed.includes('<h2') || trimmed.includes('<h3')) {
                            return trimmed
                          }
                          const withBreaks = trimmed.replace(/\n/g, '<br>')
                          return `<p class="mb-6 text-gray-700 dark:text-gray-300">${withBreaks}</p>`
                        }).join('\n')
                    }}
                  />
                </section>
              )}
            </div>
          </article>
        </div>
        </div>

      {/* Action Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 border-t border-gray-200 dark:border-gray-700">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 sm:p-8">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">
            {user ? 'What would you like to do next?' : 'Join InsightAtlas to unlock more features'}
          </h3>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-2xl mx-auto">
          <button
            onClick={handleSaveArticle}
              className={`flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border ${
                isSaved 
                  ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700' 
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
              {user ? (isSaved ? 'Saved to Library' : 'Save to Library') : 'Sign Up to Save'}
          </button>
            
          <button
            onClick={handleStartResearch}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3"
          >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            Create Your Own Research
          </button>
          <button
            onClick={handleReadFullReport}
            className="flex-1 bg-black dark:bg-white text-white dark:text-black px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base transition-all hover:shadow-lg flex items-center justify-center gap-2 sm:gap-3 border border-black dark:border-white"
            title="Read full report"
          >
            Read Full Report
          </button>
          </div>
        </div>
      </div>

      {/* Post-Read Engagement Modal */}
      {showSignupPrompt && !user && (
        <>
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 transition-all duration-300"
            onClick={() => setShowSignupPrompt(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white dark:bg-gray-900 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform transition-all duration-500 relative mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="text-center mb-4 sm:mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Enjoyed this research?
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Create your free account to unlock powerful AI research tools
                </p>
              </div>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Save and organize research articles</p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Create your own AI-powered research</p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Get personalized insights and recommendations</p>
                </div>
              </div>

              <div className="w-full space-y-3">
                <GoogleLogin 
                  onSuccess={handleGoogleLogin}
                  onError={(error) => {
                    onShowToast({
                      type: 'error',
                      title: 'Sign up failed',
                      description: error
                    })
                  }}
                />
                <AppleLogin
                  onSuccess={handleAppleLogin}
                  onError={(error) => {
                    onShowToast({
                      type: 'error',
                      title: 'Sign up failed',
                      description: error
                    })
                  }}
                />
              </div>

              <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4">
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setShowSignupPrompt(false)
                    navigate('/login')
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Log in
                </button>
              </p>
            </div>
          </div>
        </>
      )}
    </>
  )
}
